/**
 * Shared configuration schemas for visual checkpoint workflow nodes.
 *
 * @module visual-checkpoint
 * @see docs/features/test-suite-execution.md
 */

import { z } from 'zod';
import type { VisualAnchorCapture } from './visual-text.js';

export const VisualSeveritySchema = z.enum(['record', 'fail']);
export type VisualSeverity = z.infer<typeof VisualSeveritySchema>;

/** Minimal persisted fields used to decide whether two captures can be compared. */
export type VisualCapturePairFields = {
  status: 'captured' | 'capture_failed';
  storageKey: string | null;
  renderProfileId: string;
  width: number | null;
  height: number | null;
  unstable: boolean;
};

/** Returns why a capture pair cannot produce a pixel comparison, or null when it can. */
export const visualCapturePairPrecondition = (
  head: VisualCapturePairFields,
  baseline: VisualCapturePairFields
): 'unavailable' | 'incompatible' | null => {
  if (
    head.status !== 'captured' || baseline.status !== 'captured' ||
    !head.storageKey || !baseline.storageKey || head.unstable || baseline.unstable
  ) return 'unavailable';
  if (
    head.renderProfileId !== baseline.renderProfileId ||
    head.width !== baseline.width || head.height !== baseline.height
  ) return 'incompatible';
  return null;
};

export const VisualMaskSchema = z.object({
  id: z.string().uuid(),
  description: z.string().min(1),
  locator: z.string().min(1),
  reason: z.string().min(1),
  healedAt: z.string().datetime(),
  healedCaptureId: z.string().uuid(),
});
export type VisualMask = z.infer<typeof VisualMaskSchema>;

export type VisualElementAnchor = VisualAnchorCapture['elements'][number];

export const VisualCheckpointResolvedTargetSchema = z.object({
  text: z.string().min(1),
  locator: z.string().min(1),
});
export type VisualCheckpointResolvedTarget = z.infer<typeof VisualCheckpointResolvedTargetSchema>;

const optionalText = z.preprocess((value) => value === '' ? undefined : value, z.string().min(1).optional());

export const VisualCheckpointNodeConfigSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  name: z.string().min(1),
  target: optionalText,
  resolvedTarget: VisualCheckpointResolvedTargetSchema.optional(),
  expectedState: optionalText,
  severity: VisualSeveritySchema.default('record'),
  masks: z.array(VisualMaskSchema).default([]),
});
export type VisualCheckpointNodeConfig = z.infer<typeof VisualCheckpointNodeConfigSchema>;
